import React, { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from 'hooks';
import { selectCurrentTeam, selectMerchantCustomizations, showNotifier } from 'containers/app/appSlice';
import CreateEstimate from './create-edit-estimate/CreateEstimate';
import EstimateBuyersPreview from './buyers-preview/EstimateBuyersPreview';
import { NotifierTypes } from 'constants/notifierConstants';
import routesPath from 'routes/RoutesPath';
import { useCheckEstimateReferenceNumberMutation, useCreateEstimateMutation, useEditEstimateMutation } from './api';
import EstimateCreationConfirmationPopup from './components/EstimateCreationConfirmationPopup';
import EstimateErrorsPopup from './components/EstimateErrorsPopup';
import {
  CreateEditEstimateNavEnum,
  CreateEstimateErrorTypes,
  ESTIMATE_ATTRIBUTE_TYPE,
  EstimatePageState
} from './constants';
import {
  addEstimateValidationError,
  resetAutoFilledData,
  resetEstimateValidationErrors,
  selectAutoFillKey,
  selectCascadingRelatedEntityData,
  selectEstimate,
  selectEstimateAutoFilledData,
  selectEstimateCustomer,
  selectEstimateFinalAmount,
  selectEstimateReferenceNumber,
  selectInitialEstimateReferenceNumber
} from './estimatesSlice';
import {
  generateCreateEstimatePayload,
  getModifiedAutoFilledEstimateFields,
  hasDataChangesInEstimate,
  mapEstimateRiskValidationEntityToErrorType,
  validateEstimateFields
} from './utils';
import { useLazyGetTeamProfileDataDataQuery } from 'containers/team-profile/api';
import { CrossIconvector } from 'assets/icons';
import { GenericModal } from 'components';
import { logAnalyticEvent } from 'utils/analytics';
import { EstimatesCleverTapEvents } from './events';
import { useGetMerchantCustomizationsQuery } from 'containers/settings/api';
import { FeatureToggleState } from 'containers/settings/constants';
import { updateCustomizations } from 'containers/settings/invoiceSettingsSlice';
import {
  getDefaultExpiryDate,
  selectEstimateTncEdited,
  updateBillingAddress,
  updateEstimateCustomer,
  updateEstimateDefaultsFromSettings,
  updateEstimateReminders,
  updateEstimatesTnc,
  updateExpiryDate,
  updateIsSignatureEnabled,
  updateRemindersEnabled,
  updateServiceAddress
} from './estimatesSlice';
import { useLazyGetEstimateByIdQuery } from './api';
import { useLazyGetJobByIdQuery } from 'containers/jobs/api';
import {
  useGetTaxesAndFeesQuery,
  useLazyGetCustomerByIdQuery,
  useLazyGetInvoiceByIdV2Query
} from 'containers/invoices/api';
import { CustomerAddress } from 'containers/customers/customers.model';
import { useLazyGetEstimateSettingsQuery } from 'containers/settings/estimate-settings/api';
import { transformSettingApiToComponentReminders } from 'containers/settings/utils';
import DateUtils from 'utils/dateUtils';

const CreateEditEstimate: FC = () => {
  const { estimateId } = useParams<{ estimateId: string }>();
  const location = useLocation();
  const isEdit = location.pathname.includes('/edit');
  const isDuplicate = location.pathname.includes('/duplicate');

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [pageState, setPageState] = useState<EstimatePageState>(EstimatePageState.CREATE);
  const [showSyncConfirmationPopup, setShowSyncConfirmationPopup] = useState(false);
  const [showEstimateRefConfirmationPopup, setShowEstimateRefConfirmationPopup] = useState(false);
  const [isEditEstimatInProgress, setIsEditEstimateInProgress] = useState(false);

  const currentTeam = useAppSelector(selectCurrentTeam);
  const selectedEstimateReferenceNumber = useAppSelector(selectEstimateReferenceNumber);
  const initialEstimateReferenceNumber = useAppSelector(selectInitialEstimateReferenceNumber);
  const cascadingRelatedEntityData = useAppSelector(selectCascadingRelatedEntityData);
  const autoFillKey = useAppSelector(selectAutoFillKey);
  const estimateCustomer = useAppSelector(selectEstimateCustomer);
  const estimateFinalAmount = useAppSelector(selectEstimateFinalAmount);
  const autoFilledData = useAppSelector(selectEstimateAutoFilledData);
  const estimatesState = useAppSelector(selectEstimate);
  const { isFromInvoice, isFromJob, jobNumber, invoiceNumber } = cascadingRelatedEntityData ?? {};
  const merchantCustomisations = useAppSelector(selectMerchantCustomizations);
  const estimateTncEdited = useAppSelector(selectEstimateTncEdited);
  const [getTeamProfileDataDataQuery, { data: teamProfileData }] = useLazyGetTeamProfileDataDataQuery();

  const [createEstimate, { isLoading: isCreateEstimateInProgress }] = useCreateEstimateMutation();
  const [editEstimate, { isLoading: isEditEstimateInProgress }] = useEditEstimateMutation();
  const [checkEstimateRefNumberQuery, { data: estimateReferenceNumberCheckResponse }] =
    useCheckEstimateReferenceNumberMutation();

  const [showEstimateErrorsPopup, setShowEstimateErrorsPopup] = useState<{
    open: boolean;
    errors: {
      customer?: boolean;
      serviceAddress?: boolean;
      lineItem?: boolean;
      title?: boolean;
      assignees?: boolean;
      leadSource?: boolean;
      tnc?: boolean;
      totalAmount?: boolean;
    };
  }>({
    open: false,
    errors: {}
  });

  // API hooks
  const {
    data: merchantCustomisationsData,
    isSuccess: merchantCustomisationsSuccess,
    isFetching: isFetchingMerchantCustomisations
  } = useGetMerchantCustomizationsQuery({ teamId: currentTeam?.id?.toString() }, { skip: !currentTeam?.id });

  const { isLoading: isLoadingTaxesAndFees } = useGetTaxesAndFeesQuery(
    { teamId: currentTeam?.id },
    { skip: !currentTeam?.id }
  );

  const [getEstimateById] = useLazyGetEstimateByIdQuery();
  const [getJobById] = useLazyGetJobByIdQuery();
  const [getInvoiceById] = useLazyGetInvoiceByIdV2Query();
  const [getCustomerByIdQuery, { isFetching: isFetchingCustomer }] = useLazyGetCustomerByIdQuery();
  const [getEstimateSettings] = useLazyGetEstimateSettingsQuery();

  const queryParameters = new URLSearchParams(location.search);
  const invoiceId = queryParameters.get('invoiceId');
  const jobId = queryParameters.get('jobId');
  const customerId = queryParameters.get('customerId');

  useEffect(() => {
    if (isEdit) {
      logAnalyticEvent(EstimatesCleverTapEvents.webEstimateEditPageView);
    } else {
      logAnalyticEvent(EstimatesCleverTapEvents.webEstimateCreatePageView);
    }
  }, []);

  useEffect(() => {
    if (currentTeam?.id) {
      getTeamProfileDataDataQuery({ teamId: currentTeam?.id.toString() });
    }
  }, [currentTeam]);

  // Initialize merchant customizations and estimate TnC
  useEffect(() => {
    if (merchantCustomisationsSuccess && merchantCustomisationsData) {
      dispatch(updateCustomizations(merchantCustomisationsData));
      dispatch(
        updateIsSignatureEnabled(
          merchantCustomisationsData.customerSignatureConfig === FeatureToggleState.ALWAYS_ON ||
            merchantCustomisationsData.customerSignatureConfig === FeatureToggleState.ON_BY_DEFAULT
        )
      );
      const termsAndConditions = merchantCustomisationsData?.estimateTnc;
      if (termsAndConditions?.tncId && !estimateTncEdited) {
        dispatch(updateEstimatesTnc(termsAndConditions.tnc));
      }
    }
  }, [merchantCustomisationsSuccess, merchantCustomisationsData, dispatch]);

  // Fetch customer by ID
  useEffect(() => {
    if (customerId && currentTeam?.id) {
      const getCustomerById = async () => {
        const response = await getCustomerByIdQuery({ teamId: currentTeam.id, customerId });
        if (response.data) {
          dispatch(
            updateEstimateCustomer({
              ...response.data,
              ID: response.data.id,
              billingAddress: response.data.billingAddress as CustomerAddress,
              serviceAddress: response.data.serviceAddress as CustomerAddress[]
            })
          );
          dispatch(updateServiceAddress(response.data?.serviceAddress[0] as CustomerAddress));
          dispatch(updateBillingAddress(response.data?.billingAddress as CustomerAddress));
        }
      };
      getCustomerById();
    }
  }, [customerId, currentTeam?.id, dispatch, getCustomerByIdQuery]);

  // Fetch estimate by ID
  useEffect(() => {
    if ((isEdit || isDuplicate) && estimateId && currentTeam?.id && !isEditEstimatInProgress) {
      getEstimateById({ teamId: currentTeam.id, estimateId, isFromEdit: true });
      setIsEditEstimateInProgress(true);
    }
  }, [isEdit, isDuplicate, estimateId, currentTeam?.id, isEditEstimatInProgress, getEstimateById]);

  // Fetch job by ID
  useEffect(() => {
    if (jobId && currentTeam?.id) {
      getJobById({ jobId, teamId: currentTeam.id, isFromEstimate: true });
    }
  }, [jobId, currentTeam?.id, getJobById]);

  // Fetch invoice by ID
  useEffect(() => {
    if (invoiceId && currentTeam?.id) {
      getInvoiceById({ invoiceId, teamId: currentTeam.id, isFromEstimate: true });
    }
  }, [invoiceId, currentTeam?.id, getInvoiceById]);

  // Fetch estimate settings
  useEffect(() => {
    if (currentTeam?.id) {
      getEstimateSettings({ teamId: currentTeam.id }).then(response => {
        if (response.data) {
          const defaultReminders = transformSettingApiToComponentReminders(response.data.data.reminders || [], false);
          const settingsRemindersEnabled = response.data.data.remindersEnabled || false;

          // Store default reminders and settings config in Redux (for both new and edit modes)
          dispatch(
            updateEstimateDefaultsFromSettings({
              reminders: defaultReminders,
              remindersEnabled: settingsRemindersEnabled,
              expiryDuration: response.data.data.expiry?.duration
            })
          );

          // For new estimates only: set default reminders, enabled state, and expiry date
          if (!isEdit && !isDuplicate) {
            dispatch(updateEstimateReminders(defaultReminders));
            dispatch(updateRemindersEnabled(settingsRemindersEnabled));

            // Set expiry date based on duration from settings (fallback to 60 days)
            const expiryDuration = response.data.data.expiry?.duration;
            dispatch(
              updateExpiryDate(getDefaultExpiryDate(DateUtils.getStartOfDay(DateUtils.getNow()), expiryDuration))
            );
          }
        }
      });
    }
  }, [currentTeam?.id, isEdit, isDuplicate, dispatch, getEstimateSettings]);

  const checkEstimateReferenceNumber = async (value: string) => {
    const result = await checkEstimateRefNumberQuery({
      teamId: currentTeam.id,
      body: { value, attribute: ESTIMATE_ATTRIBUTE_TYPE.REFERENCE_NUMBER }
    }).unwrap();
    return result;
  };

  const handleGoBack = () => {
    setPageState(EstimatePageState.CREATE);
  };

  const handlePreview = () => {
    const isPageIncomplete = handleEstimateValidations();
    if (isPageIncomplete) {
      return;
    }
    setPageState(EstimatePageState.PREVIEW);
  };

  const handleEstimateValidations = (): boolean => {
    const { isPageIncomplete, errors } = validateEstimateFields();

    if (isPageIncomplete) {
      setShowEstimateErrorsPopup({ open: true, errors });
    }
    return isPageIncomplete;
  };

  const handleSave = async () => {
    const isPageIncomplete = handleEstimateValidations();
    if (isPageIncomplete) {
      return;
    }
    const estimateRefNumber = selectedEstimateReferenceNumber.trim();
    if (estimateRefNumber && estimateRefNumber !== initialEstimateReferenceNumber.trim()) {
      const { data } = await checkEstimateReferenceNumber(estimateRefNumber);
      const { isExists } = data ?? {};
      if (isExists) {
        setShowEstimateRefConfirmationPopup(true);
        return;
      }
    }
    if ((isFromJob || isFromInvoice) && hasDataChangesInEstimate()) {
      setShowSyncConfirmationPopup(true);
      return;
    }
    handleSaveEstimate();
  };

  const handleSaveEstimateAfterRefNumberConfirmation = () => {
    if ((isFromJob || isFromInvoice) && hasDataChangesInEstimate()) {
      setShowSyncConfirmationPopup(true);
      return;
    }
    handleSaveEstimate();
  };

  const scrollToEstimateValidationTab = (errors: { type: CreateEstimateErrorTypes; errorMsg: string }[]) => {
    if (!errors?.length) return;
    const validationMap: Record<string, CreateEstimateErrorTypes[]> = {
      [CreateEditEstimateNavEnum.CUSTOMER]: [
        CreateEstimateErrorTypes.CUSTOMER,
        CreateEstimateErrorTypes.SERVICE_ADDRESS
      ],
      [CreateEditEstimateNavEnum.ESTIMATE_DETAILS]: [CreateEstimateErrorTypes.ESTIMATE_TITLE],
      [CreateEditEstimateNavEnum.LINE_ITEMS]: [CreateEstimateErrorTypes.LINE_ITEMS]
    };
    const tabKey = Object.keys(validationMap).find(key => errors.some(err => validationMap[key].includes(err.type)));
    const tabElem = tabKey ? document.getElementById(tabKey) : null;
    tabElem?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
  };

  const handleSaveEstimate = async () => {
    setShowEstimateRefConfirmationPopup(false);
    try {
      let result;
      let autoFillModifiedFields: string[] = [];

      if (isEdit) {
        const estimateData = generateCreateEstimatePayload({ isEdit: true });
        result = await editEstimate({ teamId: currentTeam.id, estimateId, payload: estimateData }).unwrap();
      } else {
        // Calculate modified fields once for both payload and analytics
        autoFillModifiedFields = autoFilledData
          ? getModifiedAutoFilledEstimateFields(autoFilledData, estimatesState)
          : [];
        const estimateData = generateCreateEstimatePayload({ modifiedFields: autoFillModifiedFields, isEdit: false });
        result = await createEstimate({ teamId: currentTeam.id, payload: estimateData }).unwrap();
      }

      const payload = (result as { data?: Record<string, unknown> }).data ?? result;
      const payloadWithRisk = payload as {
        isRiskValidationFailed?: boolean;
        riskValidation?: { errors: { entity: string; message: string }[] };
        billRiskValidation?: { errors: { entity: string; message: string }[] };
      };
      const riskValidationPayload = payloadWithRisk.riskValidation ?? payloadWithRisk.billRiskValidation;
      const isRiskFailed =
        Boolean(payloadWithRisk.isRiskValidationFailed) && (riskValidationPayload?.errors?.length ?? 0) > 0;

      if (isRiskFailed && riskValidationPayload?.errors?.length) {
        const riskValidation = riskValidationPayload;
        dispatch(resetEstimateValidationErrors());
        const errors: { type: CreateEstimateErrorTypes; errorMsg: string }[] = [];
        riskValidation.errors.forEach((err: { entity: string; message: string }) => {
          const type = mapEstimateRiskValidationEntityToErrorType(err.entity);
          dispatch(addEstimateValidationError({ type, errorMsg: err.message }));
          errors.push({ type, errorMsg: err.message });
        });
        scrollToEstimateValidationTab(errors);
        return;
      }

      const estimateIdFromResponse =
        (payload as { ID?: string; estimateId?: string }).estimateId ??
        (payload as { ID?: string; estimateId?: string }).ID;
      if (estimateIdFromResponse) {
        // Log CleverTap event for successful estimate creation
        if (!isEdit) {
          logAnalyticEvent(EstimatesCleverTapEvents.webEstimatesCreateEstimateSave, {
            teamId: currentTeam.id.toString(),
            storeName: currentTeam.storeName,
            customerId: estimateCustomer?.ID || '',
            customerName: estimateCustomer?.name || '',
            amount: estimateFinalAmount.toString(),
            isAutofilled: !!autoFillKey,
            ...(autoFillKey && { autofillKey: autoFillKey }),
            ...(autoFillKey
              ? {
                  autofillFieldModified: autoFillModifiedFields.length > 0,
                  autoFilledModifiedFields: autoFillModifiedFields?.join(', ')
                }
              : {})
          });
        }

        // Reset autofilled data if it exists
        if (autoFillKey) {
          dispatch(resetAutoFilledData());
        }

        if (location.state?.returnTo) {
          navigate(location.state.returnTo, {
            state: location.state.returnToState || null,
            replace: true
          });
        } else {
          navigate(`${routesPath.ESTIMATES}?shareEstimate=${estimateIdFromResponse}`);
        }
        dispatch(
          showNotifier({
            message: {
              primaryMessage: `Estimate ${isEdit ? 'updated' : 'created'} successfully!`,
              secondaryMessage: '',
              isMessageHtml: false
            },
            type: NotifierTypes.SUCCESS,
            showClose: true
          })
        );
      }
    } catch (error) {
      dispatch(
        showNotifier({
          message: {
            primaryMessage: `Failed to ${isEdit ? 'update' : 'create'} estimate. Please try again.`,
            secondaryMessage: '',
            isMessageHtml: false
          },
          type: NotifierTypes.ERROR,
          showClose: true
        })
      );
    }
  };

  const getSyncPopupTitleAndSubtitle = (type: 'title' | 'subtitle') => {
    const hasJob = Boolean(isFromJob || jobNumber);
    const hasInvoice = Boolean(isFromInvoice || invoiceNumber);
    const relatedEntities = [hasJob && 'job', hasInvoice && 'invoice'].filter(Boolean).join(' and ');
    const associatedEntities = [hasJob && `job ${jobNumber}`, hasInvoice && `invoice ${invoiceNumber}`]
      .filter(Boolean)
      .join(' and ');
    const title = `Save and sync changes to the related ${relatedEntities}?`;
    const subtitle = `Changes in your estimate will be applied to the associated ${associatedEntities} as well.`;
    return type === 'title' ? title : subtitle;
  };

  if (pageState === EstimatePageState.PREVIEW) {
    return (
      <EstimateBuyersPreview
        goBack={handleGoBack}
        getTeamProfileData={teamProfileData}
        onCreateAndShare={handleSave}
        isCreating={false}
      />
    );
  }

  return (
    <>
      {pageState === EstimatePageState.CREATE && (
        <CreateEstimate
          onPreview={handlePreview}
          onCreateAndShare={handleSave}
          isSaveInProgress={isCreateEstimateInProgress || isEditEstimateInProgress}
          isLoadingTaxesAndFees={isLoadingTaxesAndFees}
          isFetchingCustomer={isFetchingCustomer}
          isFetchingMerchantCustomisations={isFetchingMerchantCustomisations}
          merchantCustomisationsSuccess={merchantCustomisationsSuccess}
        />
      )}

      {showEstimateErrorsPopup.open && (
        <EstimateErrorsPopup
          onClose={() => setShowEstimateErrorsPopup({ open: false, errors: {} })}
          errorFields={showEstimateErrorsPopup.errors}
          maximumAmount={+merchantCustomisations?.maximumAmount || 0}
          minimumAmount={+merchantCustomisations?.minimumAmount || 0}
          onContinue={() => setShowEstimateErrorsPopup({ open: false, errors: {} })}
        />
      )}
      {showEstimateRefConfirmationPopup && (
        <EstimateCreationConfirmationPopup
          isSaveItemLoading={isCreateEstimateInProgress || isEditEstimateInProgress}
          onContinue={handleSaveEstimateAfterRefNumberConfirmation}
          closeSaveConfirmation={() => setShowEstimateRefConfirmationPopup(false)}
          estimateRefNumber={selectedEstimateReferenceNumber}
          conflictingItems={estimateReferenceNumberCheckResponse?.data?.estimateNumber}
        />
      )}

      {showSyncConfirmationPopup && (
        <GenericModal
          showModal={true}
          title={getSyncPopupTitleAndSubtitle('title')}
          titleStyle="text-center"
          subTitleStyle="text-center text-primaryText text-sbase font-normal"
          subTitle={getSyncPopupTitleAndSubtitle('subtitle')}
          primaryBtnLabel="Yes, continue"
          primaryBtnId="sync-job-invoice"
          primaryBtnStyle="mt-2"
          onPrimaryBtnClick={handleSaveEstimate}
          secondaryBtnId="cancel-sync-job-invoice"
          secondaryBtnStyle="border-none shadow-none hover:bg-transparent"
          secondaryBtnLabel="Cancel"
          onSecondaryBtnClick={() => setShowSyncConfirmationPopup(false)}
          closeIconStyle="top-3 right-3"
          CloseIcon={() => (
            <CrossIconvector
              height={18}
              width={18}
              title="sync-popup-close"
              className="cursor-pointer"
              onClick={() => setShowSyncConfirmationPopup(false)}
            />
          )}
          isActionInProgress={isCreateEstimateInProgress || isEditEstimateInProgress}
          height="min-h-[262px]"
          className="!px-5 !py-7 !pb-5"
        />
      )}
    </>
  );
};

export default CreateEditEstimate;
